import { DeleteForever, ExpandMore } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import BetslipErrorMessage from '../../../atoms/BetslipErrorMessage';
import useAuthForms from '../../../../hooks/useAuthForms';
import useLocalization from '../../../../hooks/useLocalization';
import NeobetPlaceBetButton from './NeobetPlaceBetButton';
import type { NeobetBetslipDrawerController } from '../../../../hooks/components/useNeobetBetslipDrawer';
import NeobetBonusTracker from './NeobetBonusTracker';
import NeobetOddsChangesSelect from './NeobetOddsChangesSelect';
import NeobetBetslipSummary from './NeobetBetslipSummary';
import NeobetBetslipSystemSection from './NeobetBetslipSystemSection';
import NeobetBetslipStakeInput from './NeobetBetslipStakeInput';
import NeobetBetslipStakeMessage from './NeobetBetslipStakeMessage';
import NeobetRaisedActionButton from './NeobetRaisedActionButton';

const styles = {
  footer: {
    px: 1.25,
    py: 1.25,
    borderTop: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'neutral.700',
    display: 'grid',
    gap: 1,
  },
  footerSystemSection: {
    display: 'grid',
    gap: 0.75,
  },
  footerSystemTriggerButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    width: 'fit-content',
    minWidth: 0,
    p: 0,
    border: 0,
    backgroundColor: 'transparent',
    color: 'neutral.300',
    typography: 'body4',
    fontWeight: 700,
    lineHeight: 1.2,
    '&:hover': {
      color: 'common.white',
      backgroundColor: 'transparent',
    },
    '&:focus-visible': {
      outline: 'none',
      color: 'common.white',
    },
  },
  footerSystemTriggerIcon: {
    fontSize: 18,
    flexShrink: 0,
  },
  updateStakesHelper: {
    color: 'neutral.300',
    typography: 'body4',
    fontWeight: 700,
    textAlign: 'center',
  },
  presetRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 0.75,
  },
  presetButton: (active: boolean) => ({
    minWidth: 0,
    borderRadius: 2.5,
    height: 30,
    px: 0,
    py: 0.875,
    border: '1px solid',
    borderColor: active ? 'primary.main' : 'divider',
    backgroundColor: active ? 'primary.main' : 'neutral.600',
    color: active ? 'background.default' : 'text.primary',
    fontWeight: 800,
  }),
  footerActionsRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 1,
  },
  removeAllIconButton: {
    width: 48,
    minWidth: 48,
    height: 48,
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'neutral.600',
    color: 'neutral.300',
  },
  oddsChanges: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: 'neutral.600',
    border: '1px solid',
    borderRadius: 1,
    borderColor: 'neutral.500',
    px: 1.5,
    pl: 2,
    alignItems: 'center',
    width: '100%',
  },
  oddsChangesSelect: {
    '&.MuiInputBase-sizeSmall:has(.MuiSelect-select)': {
      border: 'none',
    },
  },
  signInButton: {
    width: '100%',
    height: 48,
  },
} as const;

type NeobetBetslipFooterProps = {
  controller: NeobetBetslipDrawerController;
};

const NeobetBetslipFooter = ({ controller }: NeobetBetslipFooterProps) => {
  const { t } = useLocalization();
  const { openLoginForm } = useAuthForms();
  const {
    bets,
    showSystemBetInputs,
    toggleSystemBetInputs,
    combinations,
    lastCombinationKey,
    stakeAmounts,
    updateStakeAmount,
    validateStake,
    handleStakeChange,
    primaryStakeMode,
    primaryStakeKey,
    primaryStakeValue,
    primaryStakeError,
    setPrimaryStakeValue,
    applyPresetAmount,
    isPresetActive,
    presetAmounts,
    updateStakesHelperMessageKey,
    selectedWallet,
    betslipTicketType,
    isSystemTicket,
    hasBetBuilderSelection,
    totalOdds,
    bonusAmount,
    totalWinningAmount,
    showInsufficientFunds,
    errors,
    isAuthenticated,
    stakeErrors,
    totalStakeAmount,
    updateStakes,
    isPlaceBetButtonDisabled,
    disabledSystems,
    discardBetslip,
  } = controller;

  const hasBets = bets.length > 0;
  const hasSystemTrigger = bets.length > 1 && !hasBetBuilderSelection;
  const isPrimaryStakeReady = primaryStakeMode === 'single' || Boolean(primaryStakeKey);
  const systemTriggerLabel = `${t('multiple', 'Multiple')}/${t('system', 'System')}`;
  const primaryStakeErrorType = primaryStakeError?.errorType;

  return (
    <Box sx={styles.footer} data-testid='neobet-betslip-footer-composer'>
      {hasSystemTrigger ? (
        <Box sx={styles.footerSystemSection}>
          <Box
            component='button'
            type='button'
            data-testid='neobet-betslip-system-trigger'
            title={systemTriggerLabel}
            aria-expanded={showSystemBetInputs}
            onClick={toggleSystemBetInputs}
            sx={styles.footerSystemTriggerButton}
          >
            <Box component='span'>{systemTriggerLabel}</Box>
            <ExpandMore sx={styles.footerSystemTriggerIcon} />
          </Box>

          <NeobetBetslipSystemSection
            showSystemBetInputs={showSystemBetInputs}
            combinations={combinations}
            lastCombinationKey={lastCombinationKey}
            stakeAmounts={stakeAmounts}
            stakeErrors={stakeErrors}
            updateStakeAmount={updateStakeAmount}
            validateStake={validateStake}
            handleStakeChange={handleStakeChange}
          />
        </Box>
      ) : null}

      <NeobetBetslipStakeInput
        value={primaryStakeValue}
        onChange={setPrimaryStakeValue}
        size='large'
        helperMessage={
          primaryStakeErrorType ? (
            <NeobetBetslipStakeMessage
              errorType={primaryStakeErrorType}
              ticketType={bets.length === 1 && bets[0]?.isLive ? 'inPlay' : betslipTicketType}
              selectedWallet={selectedWallet}
              testId='neobet-betslip-primary-stake-message'
            />
          ) : undefined
        }
        disabled={!hasBets || !isPrimaryStakeReady}
        testId='neobet-betslip-primary-stake-input'
      />

      <Box sx={styles.presetRow}>
        {presetAmounts.map((amount) => (
          <Button
            key={amount}
            type='button'
            data-testid={`neobet-betslip-preset-amount-${amount}`}
            aria-pressed={isPresetActive(amount)}
            sx={styles.presetButton(isPresetActive(amount))}
            onClick={() => applyPresetAmount(amount)}
            disabled={!hasBets || !isPrimaryStakeReady}
          >
            {amount}
          </Button>
        ))}
      </Box>

      <NeobetBonusTracker selectedWallet={selectedWallet} isSystemTicket={isSystemTicket} />
      <NeobetBetslipSummary totalOdds={totalOdds} bonusAmount={bonusAmount} totalWinningAmount={totalWinningAmount} />

      {showInsufficientFunds ? <BetslipErrorMessage message={t('insufficientFunds', 'Insufficient funds')} /> : null}
      {errors.map((message, index) => (
        <BetslipErrorMessage key={`${message}-${index}`} message={message} />
      ))}

      {!isAuthenticated ? (
        <NeobetRaisedActionButton label={t('login.title')} onClick={openLoginForm} sx={styles.signInButton} />
      ) : (
        <>
          {updateStakesHelperMessageKey ? (
            <Typography sx={styles.updateStakesHelper} data-testid='neobet-betslip-update-stakes-helper'>
              {t(updateStakesHelperMessageKey)}
            </Typography>
          ) : null}
          <NeobetPlaceBetButton
            stakeErrors={stakeErrors}
            totalStakeAmount={totalStakeAmount}
            updateStakes={updateStakes}
            isDisabled={isPlaceBetButtonDisabled}
            disabledSystems={disabledSystems}
          />
          <Box sx={styles.footerActionsRow}>
            <IconButton sx={styles.removeAllIconButton} onClick={discardBetslip} disabled={!hasBets}>
              <DeleteForever />
            </IconButton>
            <Box sx={styles.oddsChanges}>
              <NeobetOddsChangesSelect selectSx={styles.oddsChangesSelect} />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default NeobetBetslipFooter;
